<template>
  <!-- Delegated listener for rendered links; pressing Enter on a link also dispatches click, so keyboard users are covered. -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
  <div :class="[MARKDOWN_TYPOGRAPHY, BOOTSTRAP_INLINE_STYLES]" @click="emitLinkClick" v-html="renderedHtml" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { renderMarkdown, MARKDOWN_TYPOGRAPHY } from '@/components/wikitext/wiki_text_markdown'

// topHeadingLevel re-levels authored headings for assistive tech (h1 is announced at that level) and leaves their look alone.
const props = withDefaults(defineProps<{ text?: string, topHeadingLevel?: number }>(), { text: '', topHeadingLevel: undefined })

const emit = defineEmits<{ 'clicked:link': [link: { text: string, url: string }] }>()

// Lift main.css's 720px paragraph cap and colour links #245682, underlined so colour is not their only cue (WCAG 1.4.1).
// Links sit in :where() so a caller's own link styling (e.g. white links on a dark header) wins without needing !important.
const BOOTSTRAP_INLINE_STYLES = [
  '[&_p]:max-w-none',
  '[&_small]:text-[85%]',
  '[&_:where(a)]:text-[#245682] [&_:where(a)]:underline [&_:where(a:hover)]:text-[#132e46]',
].join(' ')

const MAX_HEADING_LEVEL = 6

function withHeadingsFrom(topLevel: number, html: string): string {
  return html.replace(/<h([1-6])(?=[\s>])/g, (tag, level: string) => {
    const announcedLevel = Math.min(Number(level) + topLevel - 1, MAX_HEADING_LEVEL)
    return `${tag} aria-level="${announcedLevel}"`
  })
}

const renderedHtml = computed(() => {
  const html = renderMarkdown(props.text)
  return props.topHeadingLevel ? withHeadingsFrom(props.topHeadingLevel, html) : html
})

const emitLinkClick = (event: MouseEvent) => {
  const link = event.target instanceof Element ? event.target.closest('a') : null
  if (link?.href && (event.currentTarget as Element).contains(link)) {
    emit('clicked:link', { text: link.text, url: link.href })
  }
}
</script>
